import React, { useState, useEffect, useContext } from 'react';
import { Context } from '../../../context';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import useStore from '../../../customHooks/useStore';
import LoginHeader from './LoginHeader';
import EulaLink from '../../shared/EulaLink';
import { completeAdminLogin } from './adminLogin';
import TrainingLogo from '../../../assets/images/training_logo.png';
import ParentLogo from '../../../assets/images/EZP_logo_8[22].png';
import SpanishLogo from '../../../assets/images/EZP_logo_ES3.png';
import useRegistrationMutation from './useRegistrationMutation';
import AuthPortalSwitcher, {
  FACILITATOR_PORTAL,
  PARENT_PORTAL,
  getAuthPortal,
} from './AuthPortalSwitcher';
import {
  ROLE_FACILITATOR,
  ROLE_PARENT,
  setStoredRole,
} from '../../../utils/authRole';

const TECH_SUPPORT_URL =
  'https://osu.az1.qualtrics.com/jfe/form/SV_eqy7DOElHo3rYh0';

const SignUp = () => {
  const history = useHistory();
  const location = useLocation();
  const { language } = useStore((state) => ({
    language: state.language,
  }));
  const context = useContext(Context);
  const [form, setForm] = useState({});
  const [useEula, setUseEula] = useState(false);
  const pathPortal =
    location.pathname === '/facilitator/sign-up'
      ? FACILITATOR_PORTAL
      : PARENT_PORTAL;
  const portal = getAuthPortal(location, pathPortal);
  const isFacilitatorPortal = portal === FACILITATOR_PORTAL;
  const signUpTitle = isFacilitatorPortal
    ? 'Facilitator Sign Up'
    : 'Parent Sign Up';
  const authHelperCopy = isFacilitatorPortal
    ? 'If you are a parent, please toggle to the parent sign up page before signing up.'
    : 'If you are a facilitator or admin, please toggle to the facilitator sign up page before signing up.';
  const signInPath = `/login?role=${portal}`;
  const resetPasswordPath = `/reset-password?role=${portal}`;
  const registrationMutation = useRegistrationMutation();
  const [frontEndErrors, setFrontEndErrors] = useState({
    passwordMismatch: false,
  });
  const registrationLanguage =
    language || window.Localize?.getLanguage?.() || 'en';

  const handleFullReplace = (value) => {
    const digitsOnly = value.replace(/[^\d]/g, '');
    const lastTenDigits = digitsOnly.slice(-10);
    const formattedPhoneNumber = lastTenDigits.replace(
      /(\d{3})(\d{3})(\d{4})/,
      '$1-$2-$3',
    );
    return formattedPhoneNumber;
  };

  const handleChangeInput = (element) => {
    const { name, value } = element;
    if (element.name === 'phone') {
      const currentVal = form['phone']?.length || 0;
      console.log(currentVal <= value.length);
      if (currentVal <= value.length || currentVal === 0) {
        if (currentVal === 0 && value >= 10) {
          setForm((prevState) => ({
            ...prevState,
            [name]: handleFullReplace(value),
          }));
        } else {
          setForm((prevState) => ({
            ...prevState,
            [name]: value
              .replace(/^(\d{3})$/g, '$1-')
              .replace(/^(\d{3}-\d{3})$/g, '$1-'),
          }));
        }
      } else {
        setForm((prevState) => ({ ...prevState, [name]: value }));
      }
    } else {
      setForm((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const getAttributes = (name, state, handleChange) => {
    return {
      name: name,
      value: state[name] || '',
      onChange: (e) => handleChange(e.target),
    };
  };

  useEffect(() => {
    if (registrationMutation.data) {
      if (isFacilitatorPortal) {
        completeAdminLogin({
          context,
          history,
          token: registrationMutation.data,
          name: form.first,
          role: ROLE_FACILITATOR,
        });
        return;
      }

      localStorage.setItem(
        'name',
        [form.first, form.last].filter(Boolean).join(' '),
      );
      localStorage.setItem('initialVideo', false);
      setStoredRole(ROLE_PARENT);
      context.setIsAdmin(false, null);
      context.login(registrationMutation.data);
      history.push('/course/overview');
    }
  }, [
    context,
    form.first,
    form.last,
    history,
    isFacilitatorPortal,
    registrationMutation.data,
  ]);

  return (
    <div className='auth-wrapper'>
      <LoginHeader
        isAFacilitator={isFacilitatorPortal}
        logedInOrSignUp='signUp'
        signInPath={signInPath}
        resetPasswordPath={resetPasswordPath}
      />
      <section>
        <img
          src={
            isFacilitatorPortal
              ? TrainingLogo
              : registrationLanguage === 'es'
                ? SpanishLogo
                : ParentLogo
          }
          alt={isFacilitatorPortal ? 'ezTraining' : 'ezParent'}
          className='logo'
        />
        <AuthPortalSwitcher portal={portal} mode='signUp' />
        <div className='login-wrap'>
          <div className='inner-wrap'>
            <h1>{signUpTitle}</h1>
            <p className='auth-helper-copy'>{authHelperCopy}</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (form.password !== form.confirmPassword) {
                  setFrontEndErrors({ passwordMismatch: true });
                  return;
                } else {
                  setFrontEndErrors({ passwordMismatch: false });
                }
                registrationMutation.mutate({
                  first: form.first || '',
                  last: form.last || '',
                  email: form.email || '',
                  phone: form.phone || '',
                  username: form.username || '',
                  password: form.password || '',
                  orgId: form.orgId || '',
                  lang: isFacilitatorPortal ? 'en' : registrationLanguage,
                  facilitator: isFacilitatorPortal,
                });
              }}
            >
              <label>
                <p>First Name</p>
                <input
                  required
                  type='text'
                  {...getAttributes('first', form, handleChangeInput)}
                />
              </label>
              <label>
                <p>Last Name</p>
                <input
                  required
                  type='text'
                  {...getAttributes('last', form, handleChangeInput)}
                />
              </label>
              <label>
                <p>Email</p>
                <input
                  required
                  type='email'
                  {...getAttributes('email', form, handleChangeInput)}
                />
              </label>
              <label>
                <p>Phone</p>
                <input
                  maxLength={12}
                  type='tel'
                  pattern='[0-9]{3}-[0-9]{3}-[0-9]{4}'
                  placeholder='555-555-5555'
                  {...getAttributes('phone', form, handleChangeInput)}
                />
              </label>
              <label>
                <p>Username</p>
                <input
                  required
                  type='text'
                  pattern='\S+'
                  title='Whitespace is not allowed!'
                  {...getAttributes('username', form, handleChangeInput)}
                />
                {isFacilitatorPortal ? (
                  <p className='grey'>Your username is provided by program staff</p>
                ) : null}
              </label>
              <label>
                <p>Password</p>
                <input
                  type='text'
                  required
                  pattern='\S+'
                  title='Whitespace is not allowed!'
                  {...getAttributes('password', form, handleChangeInput)}
                />
              </label>
              <label>
                <p>Confirm Password</p>
                <input
                  type='text'
                  required
                  pattern='\S+'
                  title='Whitespace is not allowed!'
                  {...getAttributes('confirmPassword', form, handleChangeInput)}
                />
              </label>
              {frontEndErrors.passwordMismatch && (
                <p className='error'>Passwords do not match</p>
              )}
              <label>
                <p>Organization ID</p>
                <input
                  required
                  type='text'
                  pattern='\S+'
                  title='Whitespace is not allowed!'
                  {...getAttributes('orgId', form, handleChangeInput)}
                />
                <p className='grey'>
                  Your organization ID is provided by program staff
                </p>
              </label>
              <label className='phoneLabel'>
                <input
                  type='checkbox'
                  onChange={(e) => setUseEula(e.target.checked)}
                  checked={useEula}
                />
                <span>
                  Please review the <EulaLink />. By checking this box, you
                  confirm that you have read and agree to the terms of the End
                  User License Agreement.
                </span>
              </label>
              {registrationMutation.error && (
                <p className='error'>
                  {registrationMutation.error.message === 'no match' ? (
                    <>
                      No matching facilitator username found within this
                      organization. Please check that you have the correct
                      Organization ID and try again. If you continue to
                      experience any issues, submit a tech support form{' '}
                      <a
                        href='https://osu.az1.qualtrics.com/jfe/form/SV_eqy7DOElHo3rYh0'
                        target='_blank'
                        rel='noreferrer'
                        className='underline'
                      >
                        here.
                      </a>
                    </>
                  ) : (
                    registrationMutation.error.message
                  )}
                </p>
              )}
              <div className='button-wrap'>
                <button
                  className={`button ${!useEula ? 'disabled' : ''}`}
                  type='submit'
                  disabled={!useEula || registrationMutation.isPending}
                >
                  Sign Up
                </button>
              </div>
              <a
                href={TECH_SUPPORT_URL}
                target='_blank'
                rel='noreferrer'
                className='tech-support-link'
              >
                Need Tech Support
              </a>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignUp;
